import Mod from '@/components/mod.vue'
import { i18n } from '@/i18n'
import { useUiStore } from '@/stores/ui'
import { useSessionStorage, useConfirmDialog } from '@vueuse/core'
import { merge } from 'lodash'
import { storeToRefs } from 'pinia'
import { computed, createVNode, render, h, toValue } from 'vue'

export const useFSTableCol = col => {
  const { fullscreen } = storeToRefs(useUiStore())

  return computed(() => {
    return fullscreen.value
      ? col.map(c => {
          delete c.width
          return c
        })
      : col
  })
}

const serializer = {
  read: raw => JSON.parse(raw),
  write: raw => JSON.stringify(raw),
}

export const saved = useSessionStorage('router-state', null, { serializer })

export const useRouteState = (event, route) => {
  const get = () => {
    if (!route?.fullPath) {
      return
    }
    const restoreObj = saved.value?.[route.fullPath]

    if (!restoreObj) {
      return
    }

    if (!restoreObj[event]) {
      return
    }

    const state = { ...restoreObj[event] }
    delete restoreObj[event]

    const val = { ...saved.value }

    if (!Object.keys(restoreObj).length) {
      delete val[route.fullPath]
    } else {
      val[route.fullPath] = restoreObj
    }

    saved.value = val

    return state
  }
  const set = value => {
    if (!route?.fullPath) {
      return
    }
    let restoreObj = saved.value?.[route.fullPath]

    if (!restoreObj) {
      restoreObj = {}
    }

    merge(restoreObj, { [event]: value })

    saved.value = {
      ...saved.value,
      [route.fullPath]: restoreObj,
    }
  }
  return [get, set]
}

const usedialog = type =>
  function (msg) {
    const { reveal, cancel, confirm, onCancel, onConfirm, onReveal } = useConfirmDialog()
    let el
    let vnode

    onCancel(hide)
    onConfirm(hide)

    onReveal(() => {
      el = document.createElement('div')
      const primaryBtn = h(
        'button',
        {
          type: 'button',
          class: 'btn btn-outline-primary',
          onClick: confirm,
        },
        [
          h('i', { class: 'bi bi-check-square', 'aria-hidden': 'true' }),
          type === 1 ? ` ${i18n.global.t('common.ok')}` : ` ${i18n.global.t('common.yes')}`,
        ]
      )
      const cancelBtn =
        type === 1
          ? null
          : h(
              'button',
              {
                type: 'button',
                class: 'btn btn-outline-danger',
                onClick: cancel,
              },
              [
                h('i', { class: 'bi bi-x-square', 'aria-hidden': 'true' }),
                ` ${i18n.global.t('common.no')}`,
              ]
            )
      vnode = createVNode(
        Mod,
        {
          header: true,
          close: true,
          useHistory: false,
          ['class-name']: 'confirm-modal',
          onHidden,
        },
        {
          default: toValue(msg),
          header: i18n.global.t('common.confirmTitle'),
          footer: () =>
            h('div', { class: 'hstack gap-2' }, [primaryBtn, cancelBtn].filter(Boolean)),
        }
      )
      render(vnode, el)
    })

    return async () => {
      const { isCanceled } = await reveal()
      return !isCanceled
    }

    function hide() {
      vnode.component.exposed.hide()
    }

    function onHidden() {
      render(null, el)
      vnode = null
      el = null
    }
  }

export const useConfirm = usedialog(2)
export const useAlert = usedialog(1)
export const useDeleteConfirm = useConfirm(() => i18n.global.t('common.confirmDelete'))
export const useSendToDestructionConfirm = useConfirm(() =>
  i18n.global.t('common.confirmSendToDestruction')
)
